import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import Logo from "../Logo";

const cols = [
  { title: "Product", links: [
    ["Features", "/features"], ["Pricing", "/pricing"], ["Integrations", "/integrations"],
    ["Custom Development", "/custom-development"], ["Mobile App", "/software/mobile-workforce"],
  ]},
  { title: "Solutions", links: [
    ["Manufacturing", "/industries"], ["Healthcare", "/industries"], ["IT & Software", "/industries"],
    ["Retail", "/industries"], ["Hospitality", "/industries"],
  ]},
  { title: "Software", links: [
    ["Attendance Software", "/software/attendance-management"],
    ["Payroll Software", "/software/payroll"],
    ["Leave Management", "/software/leave-management"],
    ["Employee Self Service", "/software/employee-self-service"],
    ["HR Analytics", "/software/reports-analytics"],
  ]},
  { title: "Company", links: [
    ["About", "/about"], ["FAQ", "/faq"],
    ["Contact", "/contact"], ["Book a Demo", "/contact?intent=book_demo"],
  ]},
];

export default function Footer() {
  return (
    <footer className="bg-[#1F1E4E] text-white">
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-10">
        {/* CTA Banner */}
        <div className="rounded-3xl bg-gradient-to-br from-[#2A2960] to-[#1F1E4E] border border-white/10 p-10 md:p-14 mb-16 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#00A651]/10 blur-3xl" />
          <div className="relative max-w-3xl">
            <div className="text-xs font-bold tracking-[0.2em] text-[#00A651] uppercase mb-3">Ready when you are</div>
            <h3 className="text-3xl md:text-5xl font-extrabold tracking-tighter mb-4">
              Give your HR team hours back — every single week.
            </h3>
            <p className="text-white/70 text-base md:text-lg mb-8 max-w-2xl">
              See how ClockedIn HRM automates attendance, leave, and payroll for your workforce. A 30-minute demo is all it takes.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact?intent=book_demo"
                data-testid="footer-book-demo-btn"
                className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3 transition-colors"
              >
                Book a Demo <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact?intent=request_quote"
                data-testid="footer-request-quote-btn"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-bold rounded-lg px-6 py-3 transition-colors"
              >
                Request a Quote
              </Link>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-6 gap-10 mb-12">
          <div className="md:col-span-2">
            <Logo light />
            <p className="text-sm text-white/60 mt-4 max-w-xs leading-relaxed">
              A modern, cloud-based HR & Workforce Management platform built for growing businesses and enterprises across every industry.
            </p>
            <div className="mt-6 space-y-2 text-sm text-white/70">
              <div className="flex items-center gap-2" data-testid="footer-phone"><Phone className="h-4 w-4 text-[#00A651]" /> <a href="tel:+917976117684" className="hover:text-white">+91-7976117684</a></div>
              <a
                href="https://wa.me/917976117684?text=Hi%20Clocked%20In%20One%2C%20I%27d%20like%20to%20know%20more%20about%20your%20workforce%20management%20platform."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-btn"
                className="inline-flex items-center gap-2 mt-2 bg-[#25D366] hover:bg-[#1FB955] text-white font-bold rounded-lg px-4 py-2 text-xs transition-colors"
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true"><path d="M20.52 3.48A11.87 11.87 0 0 0 12.05 0C5.5 0 .17 5.33.17 11.88c0 2.1.55 4.14 1.6 5.94L0 24l6.34-1.66a11.86 11.86 0 0 0 5.7 1.45h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.24-6.15-3.41-8.43ZM12.05 21.5h-.01a9.6 9.6 0 0 1-4.9-1.34l-.35-.21-3.76.98 1-3.67-.23-.38a9.62 9.62 0 0 1-1.47-5.1c0-5.31 4.33-9.63 9.65-9.63 2.58 0 5 1 6.82 2.83a9.6 9.6 0 0 1 2.83 6.82c0 5.31-4.33 9.7-9.63 9.7Zm5.55-7.19c-.3-.15-1.79-.88-2.07-.98-.28-.1-.48-.15-.68.15-.2.3-.78.98-.96 1.18-.18.2-.35.22-.65.08-.3-.15-1.28-.47-2.44-1.5-.9-.8-1.5-1.78-1.68-2.08-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.68-1.64-.93-2.24-.24-.58-.5-.5-.68-.51h-.58c-.2 0-.53.08-.8.38-.28.3-1.06 1.04-1.06 2.53s1.08 2.94 1.23 3.14c.15.2 2.14 3.28 5.19 4.6.72.32 1.28.5 1.72.65.72.23 1.38.2 1.9.12.58-.09 1.79-.73 2.04-1.44.25-.7.25-1.31.18-1.44-.08-.13-.28-.2-.58-.35Z"/></svg>
                Chat on WhatsApp
              </a>
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-white/50 mb-4">{c.title}</div>
              <ul className="space-y-3">
                {c.links.map(([l, h]) => (
                  <li key={l}>
                    <Link to={h} className="text-sm text-white/80 hover:text-[#00A651] transition-colors">{l}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-white/50">© {new Date().getFullYear()} ClockedIn HRM. All rights reserved.</div>
          <div className="flex gap-6 text-xs text-white/50">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white">Terms &amp; Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
